// apps/web/src/features/call-center/components/CallCenterLeadDetailDrawer.tsx
//
// Read-only detail drawer for ONE of the external agent's own leads (#1398).
//
// DANGER: external boundary — this renders the row's IN-MEMORY snapshot ONLY.
// No extra fetch, no opaque id, no live lead data / public code / status. The
// call-recording link opens in a new tab via a safe external anchor.
import { useTranslation } from 'react-i18next';
import type { CallCenterMyLeadsItem } from '@magimp/shared';
import { Drawer, DetailSheet } from '../../../ui/index.js';
import { formatDateTime } from '../../../i18n/index.js';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 'var(--s-4)' }}>
      <div style={{ fontSize: 12, color: 'var(--fg-3)', marginBottom: 4 }}>{label}</div>
      <div
        style={{
          fontSize: 14,
          color: 'var(--fg-1)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function CallCenterLeadDetailDrawer({
  narrow,
  lead,
  onClose,
}: {
  narrow: boolean;
  lead: CallCenterMyLeadsItem;
  onClose: () => void;
}) {
  const { t } = useTranslation('callCenter');
  return (
    <Drawer width={480} narrow={narrow} onClose={onClose}>
      <DetailSheet title={t('selfService.detail.title')} onClose={onClose}>
        <Field label={t('selfService.columns.phone')}>{lead.phone}</Field>
        <Field label={t('selfService.columns.name')}>{lead.name}</Field>
        <Field label={t('selfService.columns.comment')}>
          {lead.comment ? (
            lead.comment
          ) : (
            <span style={{ color: 'var(--fg-3)' }}>{t('selfService.detail.noComment')}</span>
          )}
        </Field>
        <Field label={t('selfService.columns.audioUrl')}>
          {lead.audioUrl ? (
            <a
              href={lead.audioUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cc-lead-detail-audio"
              style={{ color: 'var(--accent-1)' }}
            >
              {t('selfService.audioLink')}
            </a>
          ) : (
            <span style={{ color: 'var(--fg-3)' }}>{t('selfService.detail.noAudio')}</span>
          )}
        </Field>
        <Field label={t('selfService.columns.createdAt')}>{formatDateTime(lead.createdAt)}</Field>
      </DetailSheet>
    </Drawer>
  );
}
